import React, { useState, useCallback, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import MobileFormContentManager from "../../components/common/mobile/MobileFormContentManager";
import DesignTreePicker from "../../components/design/DesignTreePicker";
import TinyMCEeditor from "../../components/const/TinyMCEeditor";
import * as designMeta from "../../api/design/meta";
import { getDocPersons } from "../../api/papers";
import MobilePeopleCellLoader from "../../components/common/people/MobilePeopleCellLoader";

export default function Fcr_new() {
  const { id: projectId, dns: paramDns } = useParams();
  const [showDesignPicker, setShowDesignPicker] = useState(false);
  const [activeRow, setActiveRow] = useState(null);
  const reasonEditorRef = useRef(null);

  const [dns, setDns] = useState("");

  // 🔹 요청 담당자(여러명) – PeopleCell에서 사용하는 상태
  const [requestPeople, setRequestPeople] = useState([]);

  const [form, setForm] = useState({
    title: "",
    reason: "",
    attachments: [],
    details: [
      {
        id: 1,
        drawingNo: "",
        original: "",
        changed: "",
        cause: "",
      },
    ],
    alert: {
      send: true,
      urgent: false,
      departments: [], // 여기엔 user id 배열 저장
    },
  });

  /* ---------------------------------------------------
     수정 모드: 기존 meta 로드
  --------------------------------------------------- */
  useEffect(() => {
    if (!paramDns) return; // new 모드

    const load = async () => {
      try {
        const res = await designMeta.getMeta({ dns: paramDns });
        const meta = res.meta || res.data?.meta || {};

        const loadedPeople = await getDocPersons(paramDns);

        setDns(paramDns);

        setRequestPeople(loadedPeople);

        setForm({
          title: meta.title || "",
          requestDept: meta.requestDept || "",
          reason: meta.reason || "",
          attachments: meta.attachments || [],
          details: meta.details || [],
          alert: meta.alert || {
            send: true,
            urgent: false,
            departments: (loadedPeople || []).map((p) => p.id),
          },
        });
      } catch (e) {
        console.error(e);
        alert("기존 문서를 불러오지 못했습니다.");
      }
    };

    load();
  }, [paramDns]);

  /* ---------------------------------------------------
     저장 payload
  --------------------------------------------------- */
  const getBody = useCallback(() => {
    const reasonHtml = reasonEditorRef.current?.getContent() || "";

    return {
      values: {
        project: projectId, // 필수
        module: "design",
        doc_type: "FCR",
        title: form.title,

        meta: {
          title: form.title,
          reason: reasonHtml,
          details: form.details,

          alert: {
            send: form.alert.send,
            urgent: form.alert.urgent,
            departments: requestPeople.map((p) => p.id),
          },

          attachments: form.attachments,
        },
      },
    };
  }, [form, projectId, requestPeople]);

  /* ---------------------------------------------------
     핸들러
  --------------------------------------------------- */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleDetailChange = (index, key, value) => {
    setForm((prev) => {
      const updated = [...prev.details];
      updated[index][key] = value;
      return { ...prev, details: updated };
    });
  };

  const addDetailRow = () =>
    setForm((prev) => ({
      ...prev,
      details: [
        ...prev.details,
        {
          id: prev.details.length + 1,
          drawingNo: "",
          original: "",
          changed: "",
          cause: "",
        },
      ],
    }));

  const removeDetailRow = (i) =>
    setForm((prev) => ({
      ...prev,
      details: prev.details.filter((_, idx) => idx !== i),
    }));

  return (
    <div className="mobile-page-root mb-16">
      <div className="mobile-page-root-inner space-y-3">
      <MobileFormContentManager
        params={{
          module: "design",
          doc_type: "FCR",
          project: projectId,
          dns,
        }}
        getBody={getBody}
        requestPeople={requestPeople}
        onChangeId={(newDns) => setDns(newDns)}
        redirectTo={`/project/${projectId}/design/list`}
        docName={"설계변경요청서 (FCR)"}
        docDescribe={"설계 변경이 필요한 경우 아래 내용을 작성하세요."}
        summaryConfig={{
          title: "문서 요약",
          sections: [
            { label: "제목", value: form.title || "-" },
            {
              label: "요청부서/담당자",
              value: requestPeople.map((r) => r.name).join(", ") || "-",
            },
          ],
          list: [
            {
              label: "세부 변경 내역",
              items: form.details?.map(
                (d) =>
                  `${d.drawingNo || "-"}: ${d.original || "-"} → ${
                    d.changed || "-"
                  }`
              ),
            },
          ],
          footerNote: "입력한 내용이 실시간으로 반영됩니다.",
        }}
      >
        <div className="space-y-3 w-full min-w-0 max-w-full overflow-x-hidden">
          {/* 기본 정보 */}
          <div className="bg-white rounded-lg border overflow-hidden min-w-0">
            <div className="bg-gray-50 px-3 py-2 border-b">
              <div className="text-sm font-semibold text-gray-900">
                기본 정보
              </div>
            </div>
            <div className="p-3 space-y-3">
              {/* 설계변경명 */}
              <div>
                <label
                  htmlFor="title"
                  className="text-xs text-gray-600 font-medium mb-1 block"
                >
                  설계변경명
                </label>
                <input
                  id="title"
                  name="title"
                  value={form.title}
                  onChange={handleChange}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="예: 구조벽체 두께 변경 요청"
                />
              </div>

              {/* 요청 담당자 */}
              <div>
                <label className="text-xs text-gray-600 font-medium mb-1 block">
                  요청 담당자
                </label>
                <div className="border border-gray-200 rounded-lg px-3 py-2 bg-gray-50">
                  <MobilePeopleCellLoader
                    dns={paramDns}
                    value={requestPeople}
                    onChange={(list) => setRequestPeople(list)}
                  />
                </div>
              </div>

              {/* 변경 사유 */}
              <div>
                <label className="text-xs text-gray-600 font-medium mb-1 block">
                  변경 사유
                </label>
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <TinyMCEeditor
                    height={250}
                    initialValue={form.reason}
                    editorRef={reasonEditorRef}
                    onChange={(v) =>
                      setForm((prev) => ({ ...prev, reason: v }))
                    }
                  />
                </div>
              </div>
            </div>
          </div>

          {/* 세부 변경 내역 */}
          <div className="bg-white rounded-lg border overflow-hidden min-w-0">
            <div className="bg-gray-50 px-3 py-2 border-b flex items-center justify-between">
              <div className="text-sm font-semibold text-gray-900">
                세부 변경 내역
              </div>
              <button
                onClick={addDetailRow}
                className="btn-create-sm flex-shrink-0 whitespace-nowrap"
              >
                + 행 추가
              </button>
            </div>
            <div className="p-3 space-y-3">
              {form.details.map((row, i) => (
                <div
                  key={row.id}
                  className="border border-gray-200 rounded-lg p-3 bg-gray-50 space-y-2"
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-gray-700">
                      No. {i + 1}
                    </span>
                    {form.details.length > 1 && (
                      <button
                        onClick={() => removeDetailRow(i)}
                        className="btn-delete-sm flex-shrink-0 whitespace-nowrap"
                      >
                        삭제
                      </button>
                    )}
                  </div>

                  {/* 도면번호 */}
                  <div>
                    <label className="text-xs text-gray-600 font-medium mb-1 block">
                      도면번호
                    </label>
                    {row.drawingFileId ? (
                      <div className="flex items-center justify-between w-full border border-gray-200 rounded-lg px-3 py-2 bg-white">
                        <span className="truncate flex-1 text-sm">
                          {row.drawingNo}
                        </span>
                        <button
                          onClick={() => {
                            handleDetailChange(i, "drawingFileId", null);
                            handleDetailChange(i, "drawingFileUrl", "");
                            handleDetailChange(i, "drawingNo", "");
                          }}
                          className="ml-2 text-red-500 hover:text-red-600 text-xs flex-shrink-0"
                          title="선택 해제"
                        >
                          ✕
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={row.drawingNo}
                          onChange={(e) =>
                            handleDetailChange(i, "drawingNo", e.target.value)
                          }
                          className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="도면번호 또는 선택"
                        />
                        <button
                          onClick={() => {
                            setActiveRow(i);
                            setShowDesignPicker(true);
                          }}
                          className="px-3 py-2 border border-gray-200 rounded-lg text-sm hover:bg-gray-100 flex-shrink-0 whitespace-nowrap"
                          title="설계도서에서 선택"
                        >
                          📂
                        </button>
                      </div>
                    )}
                  </div>

                  {/* 변경 전 내용, 변경 후 내용 */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div>
                      <label className="text-xs text-gray-600 font-medium mb-1 block">
                        변경 전 내용
                      </label>
                      <input
                        type="text"
                        value={row.original}
                        onChange={(e) =>
                          handleDetailChange(i, "original", e.target.value)
                        }
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-600 font-medium mb-1 block">
                        변경 후 내용
                      </label>
                      <input
                        type="text"
                        value={row.changed}
                        onChange={(e) =>
                          handleDetailChange(i, "changed", e.target.value)
                        }
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>

                  {/* 변경 사유 */}
                  <div>
                    <label className="text-xs text-gray-600 font-medium mb-1 block">
                      변경 사유
                    </label>
                    <input
                      type="text"
                      value={row.cause}
                      onChange={(e) =>
                        handleDetailChange(i, "cause", e.target.value)
                      }
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {showDesignPicker && (
          <DesignTreePicker
            open={showDesignPicker}
            onClose={() => setShowDesignPicker(false)}
            onConfirm={(file) => {
              handleDetailChange(
                activeRow,
                "drawingNo",
                file.name || file.title
              );
              handleDetailChange(activeRow, "drawingFileId", file.id);
              handleDetailChange(
                activeRow,
                "drawingFileUrl",
                file.file_url || file.url || ""
              );
            }}
          />
        )}
      </MobileFormContentManager>
      </div>
    </div>
  );
}
